import cachedFetch from '../utility-functions/cached-fetch';
import { queryStringFromObject } from '../utility-functions/utility-functions';

export interface BasketUpdateQuantity {
  errors: any;
  success: boolean;
  response: BasketUpdateQuantityResponse;
  updatedDataLayer: string;
}

export interface BasketUpdateQuantityResponse {
  basketGuid: any;
  basketLineId: number;
  quantity: number;
  voucherCode: any;
  success: boolean;
  succeeded: boolean;
  errors: BasketUpdateQuantityError[];
}

export interface BasketUpdateQuantityError {
  code: string;
  description: string;
}

const BASKET_URL = '/basket/api';
const QUICK_BASKET_URL = '/basket/quickbasket';

const addMultipleProducts = (productArr) => {
  const fetchArray = productArr.map((singleProduct) => {
    return addSingleProduct(singleProduct);
  });

  return Promise.all(fetchArray).catch((error) => {
    return { message: 'error fetching adding or more products', errorResponse: error };
  });
};

const addSingleProduct = (singleProduct) => {
  return fetch(`${BASKET_URL}/add?${queryStringFromObject(singleProduct)}`, { credentials: 'include' })
    .then((res) => res.json())
    .then((product) => {
      if (product.success === false) return { ...singleProduct, ...product };
      return product;
    })
    .catch((error) => {
      return { message: 'server error', error };
    });
};

const modifyBagItem = (productToModify) => {
  let url;

  typeof productToModify === 'string'
    ? (url = productToModify)
    : (url = `${BASKET_URL}/${productToModify.action}?${queryStringFromObject(productToModify.product)}`);

  return fetch(url, { credentials: 'include' })
    .then((resp) => resp.json())
    .then((response) => {
      if (response.success === false) return { ...productToModify, ...response };
      return response;
    })
    .catch((error) => {
      return { message: 'server error', error };
    });
};

const modifyQuickBagItem = (productToModify): Promise<BasketUpdateQuantity> => {
  let url;

  typeof productToModify === 'string'
    ? (url = productToModify)
    : (url = `${QUICK_BASKET_URL}/${productToModify.action}?${queryStringFromObject(productToModify.product)}`);

  return fetch(url, { credentials: 'include' })
    .then((resp) => resp.json())
    .then((response: BasketUpdateQuantity) => {
      if (response.success === false) return { ...productToModify, ...response };
      return response;
    })
    .catch((error) => {
      return {
        success: false,
        response: {
          errors: [
            {
              code: '500',
              description: 'Server error: ' + error.message || 'Unknown error'
            }
          ]
        }
      } as BasketUpdateQuantity;
    }) as Promise<BasketUpdateQuantity>;
};

export interface IAddToBagParams {
  price?: number;
  productId: string;
  stockId: string;
  quantity: number;
}
// Add to bag
export const addToBag = (productToAdd: JSON | IAddToBagParams) => {
  if (Array.isArray(productToAdd)) return addMultipleProducts(productToAdd);
  return addSingleProduct(productToAdd);
};

// Below methods can take productID or full url as an argument.

// Remove from bag
export const removeFromBag = (productId) => {
  const query = typeof productId === 'string' ? productId : { action: 'delete', product: { basketLineId: productId } };
  return modifyBagItem(query);
};

// Increase quantity
export const increaseQuantity = (productId, quantity: number = null) => {
  const query =
    typeof productId === 'string'
      ? productId
      : { action: 'increase', product: { shoppingBasketId: productId, quantity: quantity ? quantity : 1 } };
  return modifyBagItem(query);
};

// Increase quantity basket
export const increaseQuantityBasket = (productId, quantity: number = null) => {
  const query =
    typeof productId === 'string'
      ? productId
      : { action: 'increase', product: { basketLineId: productId, quantity: quantity ? quantity : 1 } };
  return modifyQuickBagItem(query);
};

// Decrease quantity
export const decreaseQuantity = (productId, quantity: number = null) => {
  const query =
    typeof productId === 'string'
      ? productId
      : { action: 'decrease', product: { shoppingBasketId: productId, quantity: quantity ? quantity : 1 } };
  return modifyBagItem(query);
};

// Decrease quantity basket
export const decreaseQuantityBasket = (productId, quantity: number = null) => {
  const query =
    typeof productId === 'string'
      ? productId
      : { action: 'decrease', product: { basketLineId: productId, quantity: quantity ? quantity : 1 } };
  return modifyQuickBagItem(query);
};

// Get current bag
export const getCurrentBagData = (cacheBuster: boolean = false) => {
  return cachedFetch(`${BASKET_URL}/getcurrent`, { request: { credentials: 'include' }, cacheBuster, seconds: 60 })
    .then((resp) => resp.json())
    .then((bag) => bag)
    .catch((error) => {
      return { message: 'server error', error };
    });
};
